import type { Result } from "@zxing/library";
import { ScanLineIcon } from "lucide-react";
import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { z } from "zod";
import { ScannerModal } from "@/components/modals/scanner-modal";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";
import type { submitOrderSchema } from "@/shared/schemas/submit-order";

export const NumberSelectionTable = ({
  order,
  form,
}: {
  order: OrderState;
  form: UseFormReturn<z.infer<typeof submitOrderSchema>>;
}) => {
  const { t } = useTranslation();
  const { session } = useSession();
  const [isScannerOpen, setIsScannerOpen] = useState<boolean>(false);
  const [stopStream, setStopStream] = useState<boolean>(false);

  const handleScanResult = (error: unknown, result?: Result) => {
    if (result) {
      const scannedText = result.getText() || "";
      form.setValue("imsiNumber", scannedText);
      closeScannerModal();
      toast.success(t("scanner.messages.scan-success"));
    }
    if (error) {
      toast.error(t("scanner.messages.scanner-error"));
    }
  };

  const handleScanError = (error: string | DOMException) => {
    if (typeof error !== "string" && error.name === "NotAllowedError") {
      toast.error(t("scanner.messages.camera-permission-denied"));
    } else {
      toast.error(t("scanner.messages.scanner-error"));
    }
  };

  const closeScannerModal = () => {
    // Stop the stream before closing to prevent browser freeze
    setStopStream(true);
    setTimeout(() => {
      setIsScannerOpen(false);
      setStopStream(false);
    }, 100);
  };

  return (
    <Table className="border">
      <TableHeader>
        <TableRow className="divide-x bg-muted">
          <TableHead
            className={cn(session ? "w-1/3" : "w-1/2", "text-foreground")}
          >
            {t("labels.sim-card-plan")}
          </TableHead>
          <TableHead
            className={cn(session ? "w-1/3" : "w-1/2", "text-foreground")}
          >
            {t("labels.mobile-number")}
          </TableHead>
          {session && (
            <TableHead
              className={cn(session ? "w-1/3" : "w-1/2", "text-foreground")}
            >
              {t("labels.imsi-number")}
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow className="divide-x">
          <TableCell>{order.plan.name}</TableCell>
          <TableCell>{order.msisdnNumber}</TableCell>
          {session &&
            (order.imsiNumber ? (
              <TableCell>{order.imsiNumber}</TableCell>
            ) : (
              <TableCell>
                <FormField
                  control={form.control}
                  name="imsiNumber"
                  render={({ field }) => (
                    <FormItem className="w-full sm:max-w-72">
                      <FormLabel className="sr-only">
                        {t("labels.imsi-number")}
                      </FormLabel>
                      <FormControl>
                        <ButtonGroup className="w-full">
                          <Input
                            maxLength={15}
                            placeholder={`${t("labels.imsi-number")}*`}
                            {...field}
                          />
                          <Button
                            onClick={() => setIsScannerOpen(true)}
                            type="button"
                            variant="outline"
                          >
                            <ScanLineIcon />
                          </Button>
                        </ButtonGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Scanner Modal */}
                <ScannerModal
                  isOpen={isScannerOpen}
                  onOpenChange={closeScannerModal}
                  onScanError={handleScanError}
                  onScanResult={handleScanResult}
                  stopStream={stopStream}
                />
              </TableCell>
            ))}
        </TableRow>
      </TableBody>
    </Table>
  );
};
